import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  PauseIcon,
  PlayIcon,
  XMarkIcon,
  CheckCircleIcon,
  ClockIcon,
  CurrencyYenIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';
import { API_BASE } from '../../lib/config';

const JobManagement = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/jobs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        let data = await response.json();
        // Map backend fields to frontend expected fields if necessary
        // Backend: title, employer_name, hourly_rate...
        // Frontend expects camelCase: employerName, hourlyRate
        data = data.map(job => ({
          ...job,
          employerName: job.employer_name || 'Unknown',
          hourlyRate: job.hourly_rate,
          postedAt: job.created_at,
          startDate: job.work_date,
          // flagReason, flagged might not be in backend yet, default to false
          flagged: job.status === 'flagged',
          views: 0, // Not in backend yet
          currentApplicants: job.current_applicants || 0,
          maxApplicants: job.max_applicants || 0
        }));
        setJobs(data);
      } else {
        setJobs([]);
      }
    } catch (error) {
      console.error("Failed to fetch jobs", error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE}/admin/applications`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        let data = await response.json();
        data = data.map(app => ({
          ...app,
          jobTitle: app.job_title,
          jobId: app.job_id,
          applicantName: app.applicant_name,
          applicantId: app.applicant_id,
          appliedAt: app.applied_at
        }));
        setApplications(data);
      } else {
        setApplications([]);
      }
    } catch (e) {
      console.error(e);
      setApplications([]);
    }
  };

  const filteredData = () => {
    if (activeTab === 'applications') {
      return applications.filter(app => {
        const matchesSearch = app.jobTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.applicantName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || app.status === statusFilter;
        return matchesSearch && matchesStatus;
      });
    } else {
      return jobs.filter(job => {
        let statusMatch = true;
        if (activeTab === 'active') statusMatch = job.status === 'active';
        else if (activeTab === 'flagged') statusMatch = job.flagged;
        else if (activeTab === 'completed') statusMatch = job.status === 'filled';

        const searchMatch = job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          job.employerName.toLowerCase().includes(searchTerm.toLowerCase());
        const filterMatch = statusFilter === 'all' || job.status === statusFilter;

        return statusMatch && searchMatch && filterMatch;
      });
    }
  };

  const getStatusBadge = (status, type = 'job') => {
    if (type === 'application') {
      const badges = {
        pending: { class: 'bg-yellow-100 text-yellow-800', label: '選考中' },
        accepted: { class: 'bg-green-100 text-green-800', label: '採用' },
        rejected: { class: 'bg-red-100 text-red-800', label: '不採用' },
        completed: { class: 'bg-blue-100 text-blue-800', label: '完了' }
      };
      const badge = badges[status] || badges.pending;
      return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
          {badge.label}
        </span>
      );
    } else {
      const badges = {
        active: { class: 'bg-green-100 text-green-800', label: '募集中' },
        filled: { class: 'bg-blue-100 text-blue-800', label: '募集終了' },
        paused: { class: 'bg-gray-100 text-gray-800', label: '一時停止' },
        flagged: { class: 'bg-red-100 text-red-800', label: '要確認' }
      };
      const badge = badges[status] || badges.active;
      return (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${badge.class}`}>
          {badge.label}
        </span>
      );
    }
  };

  const handleJobAction = async (jobId, action) => {
    try {
      const token = localStorage.getItem('admin_token');
      let endpoint = `${API_BASE}/admin/jobs/${jobId}/status`;
      let payload = {};

      switch (action) {
        case 'pause':
          payload = { status: 'paused' };
          break;
        case 'resume':
          payload = { status: 'active' };
          break;
        case 'approve':
          // 承認はフラグ解除のアクション
          endpoint = `${API_BASE}/admin/jobs/${jobId}/flag`;
          payload = { flagged: false };
          break;
        case 'remove':
          payload = { status: 'removed' };
          break;
        default:
          return;
      }

      // If approve (unflag), we might need a different method/payload logic depending on backend
      // Assuming backend supports the status update or specific flag endpoint.
      // Based on typical implementation:
      if (action === 'approve') {
        await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      } else {
        await fetch(endpoint, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
      }

      // Optimistic Update
      const updatedJobs = jobs.map(job => {
        if (job.id === jobId) {
          if (action === 'pause') return { ...job, status: 'paused' };
          if (action === 'resume') return { ...job, status: 'active' };
          if (action === 'approve') return { ...job, flagged: false, flagReason: null };
          if (action === 'remove') return { ...job, status: 'removed' };
        }
        return job;
      });
      setJobs(updatedJobs);
      setShowJobModal(false);
      const actionLabels = { pause: '一時停止', resume: '再開', approve: '承認', remove: '削除' };
      toast.success(`求人を${actionLabels[action]}しました`);

    } catch (error) {
      console.error('Job action failed:', error);
      toast.error('操作に失敗しました');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ja-JP');
  };

  const data = filteredData();

  return (
    <div className="space-y-6">
      {/* 戻るボタン */}
      {onBack && (
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            メニューに戻る
          </button>
        </div>
      )}

      {/* タブ */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'all', label: 'すべての求人', count: jobs.length },
            { id: 'active', label: '募集中', count: jobs.filter(j => j.status === 'active').length },
            { id: 'flagged', label: '要確認', count: jobs.filter(j => j.flagged).length },
            { id: 'completed', label: '募集終了', count: jobs.filter(j => j.status === 'filled').length },
            { id: 'applications', label: '応募履歴', count: applications.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.label}
              <span className="ml-2 bg-gray-100 text-gray-600 py-0.5 px-2 rounded-full text-xs">
                {tab.count}
              </span>
            </button>
          ))}
        </nav>
      </div>

      {/* 検索・フィルター */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="flex-1 max-w-lg">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={activeTab === 'applications' ? "求人タイトル、応募者名で検索..." : "求人タイトル、事業者名で検索..."}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">すべてのステータス</option>
              {activeTab === 'applications' ? (
                <>
                  <option value="pending">選考中</option>
                  <option value="accepted">採用</option>
                  <option value="rejected">不採用</option>
                  <option value="completed">完了</option>
                </>
              ) : (
                <>
                  <option value="active">募集中</option>
                  <option value="filled">募集終了</option>
                  <option value="paused">一時停止</option>
                  <option value="flagged">要確認</option>
                </>
              )}
            </select>
            <div className="text-sm text-gray-600">
              {data.length}件
            </div>
          </div>
        </div>
      </div>

      {/* データリスト */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {activeTab === 'applications' ? (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      応募情報
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      応募者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      応募日
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      求人情報
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      事業者
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      条件
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      応募状況
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ステータス
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={activeTab === 'applications' ? 5 : 6} className="px-6 py-12 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={activeTab === 'applications' ? 5 : 6} className="px-6 py-12 text-center text-gray-500">
                    該当するデータが見つかりません
                  </td>
                </tr>
              ) : activeTab === 'applications' ? (
                data.map((application) => (
                  <tr key={application.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{application.jobTitle}</div>
                      <div className="text-sm text-gray-500">ID: {application.jobId}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <UserIcon className="w-5 h-5 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{application.applicantName}</div>
                          <div className="text-sm text-gray-500">ID: {application.applicantId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(application.appliedAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(application.status, 'application')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedJob(application);
                          setShowApplicationModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        詳細
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                data.map((job) => (
                  <tr key={job.id} className={`hover:bg-gray-50 ${job.flagged ? 'bg-red-50' : ''}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-start">
                        {job.flagged && (
                          <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" />
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{job.title}</div>
                          <div className="text-sm text-gray-500">{job.category}</div>
                          <div className="text-sm text-gray-500">投稿: {formatDate(job.postedAt)}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>{job.employerName}</div>
                      <div className="flex items-center mt-1">
                        <MapPinIcon className="w-4 h-4 mr-1" />
                        {job.location}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <CurrencyYenIcon className="w-4 h-4 mr-1" />
                        {job.hourlyRate.toLocaleString()}/時
                      </div>
                      <div className="flex items-center mt-1">
                        <ClockIcon className="w-4 h-4 mr-1" />
                        {job.duration}時間
                      </div>
                      <div className="flex items-center mt-1">
                        <CalendarIcon className="w-4 h-4 mr-1" />
                        {formatDate(job.startDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>応募: {job.currentApplicants}/{job.maxApplicants}</div>
                      <div>閲覧: {job.views}回</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(job.flagged ? 'flagged' : job.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedJob(job);
                          setShowJobModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-700 flex items-center"
                      >
                        <EyeIcon className="w-4 h-4 mr-1" />
                        詳細
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 求人詳細モーダル */}
      {showJobModal && selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-2/3 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">求人詳細情報</h3>
              <button
                onClick={() => setShowJobModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本情報 */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">求人タイトル</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.title}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">事業者名</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.employerName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">カテゴリ</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.category}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">時給</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.hourlyRate.toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">勤務地</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.location}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">勤務日時</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {formatDate(selectedJob.startDate)} ({selectedJob.duration}時間)
                  </p>
                </div>
              </div>

              {/* 求人説明 */}
              <div>
                <label className="block text-sm font-medium text-gray-700">求人内容</label>
                <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedJob.description}</p>
              </div>

              {/* フラグ情報 */}
              {selectedJob.flagged && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mr-2" />
                    <span className="text-red-800 font-medium">要確認事項</span>
                  </div>
                  <p className="text-red-700 text-sm mt-1">{selectedJob.flagReason}</p>
                </div>
              )}

              {/* 統計情報 */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">応募状況</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.currentApplicants}/{selectedJob.maxApplicants}人</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">閲覧数</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedJob.views}回</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">ステータス</label>
                  <div className="mt-1">{getStatusBadge(selectedJob.flagged ? 'flagged' : selectedJob.status)}</div>
                </div>
              </div>

              {/* アクションボタン */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                {selectedJob.flagged && (
                  <button
                    onClick={() => handleJobAction(selectedJob.id, 'approve')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    承認
                  </button>
                )}

                {selectedJob.status === 'active' && (
                  <button
                    onClick={() => handleJobAction(selectedJob.id, 'pause')}
                    className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
                  >
                    <PauseIcon className="w-4 h-4 mr-2" />
                    一時停止
                  </button>
                )}

                {selectedJob.status === 'paused' && (
                  <button
                    onClick={() => handleJobAction(selectedJob.id, 'resume')}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center"
                  >
                    <PlayIcon className="w-4 h-4 mr-2" />
                    再開
                  </button>
                )}

                <button
                  onClick={() => handleJobAction(selectedJob.id, 'remove')}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
                >
                  <XMarkIcon className="w-4 h-4 mr-2" />
                  削除
                </button>

                <button
                  onClick={() => setShowJobModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 応募詳細モーダル */}
      {showApplicationModal && selectedJob && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">応募詳細情報</h3>
              <button
                onClick={() => setShowApplicationModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">求人タイトル</label>
                <p className="mt-1 text-sm text-gray-900">{selectedJob.jobTitle}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">応募者名</label>
                <p className="mt-1 text-sm text-gray-900">{selectedJob.applicantName}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">応募日</label>
                <p className="mt-1 text-sm text-gray-900">{formatDate(selectedJob.appliedAt)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">ステータス</label>
                <div className="mt-1">{getStatusBadge(selectedJob.status, 'application')}</div>
              </div>
              {selectedJob.message && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">応募メッセージ</label>
                  <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{selectedJob.message}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowApplicationModal(false)}
                  className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  閉じる
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobManagement;